import axios from 'axios';
import { objectToCamel, objectToSnake } from 'ts-case-convert';

import { logger } from '@zep/core-logger';

import { ApiTokenStorage } from '@/module/auth';
import { getClientFingerprintId } from '@/shared/utils/fingerprint';

import { LONG_REQUEST_TIMEOUT } from '../apiUtil';
import { axiosConfig } from '../axiosConfig';

import {
  createAiServerRequestHeaders,
  extractServerName,
  getConfiguredAiServerBaseUrl,
} from './requestConfig';
import { type TrialStatusResponse } from './type';

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Unknown error';

export const AI_HTTP_CLIENT = axios.create({
  baseURL: getConfiguredAiServerBaseUrl(),
  timeout: LONG_REQUEST_TIMEOUT,
  withCredentials: true,
  headers: {
    ...axiosConfig.headers,
    Authorization: `Bearer ${ApiTokenStorage.get()}`,
  },
});

AI_HTTP_CLIENT.interceptors.request.use(async config => {
  if (config.headers) {
    const token = ApiTokenStorage.get();
    const serverName = extractServerName();
    const requestHeaders = createAiServerRequestHeaders({
      token,
      serverName,
    });

    if (requestHeaders.Authorization) {
      config.headers.Authorization = requestHeaders.Authorization;
    } else {
      delete config.headers.Authorization;
    }

    if (requestHeaders['X-Quiz-Server-Name']) {
      // AI 서버에서 server_name 을 사용해 우회적으로 인증 권한을 부여합니다.
      config.headers['X-Quiz-Server-Name'] =
        requestHeaders['X-Quiz-Server-Name'];
    }

    // Fingerprint for guest user tracking
    try {
      const fingerprintId = await getClientFingerprintId();
      const fingerprintHeaders = createAiServerRequestHeaders({
        fingerprintId,
      });

      if (fingerprintHeaders['X-Fingerprint-Id']) {
        config.headers['X-Fingerprint-Id'] =
          fingerprintHeaders['X-Fingerprint-Id'];
      } else {
        delete config.headers['X-Fingerprint-Id'];
      }
    } catch (error) {
      // fingerprint 실패 시 요청 자체는 차단하지 않는다
      logger.debug('failed_to_get_fingerprint', {
        error: getErrorMessage(error),
      });
    }
  }

  if (config.data) {
    const { file, ...rest } = config.data;

    config.data = objectToSnake(rest);
    if (file) {
      config.data.file = file;
    }
  }

  return config;
});

AI_HTTP_CLIENT.interceptors.response.use(response => {
  if (response.data) {
    if (response.data['raw_response']) {
      console.warn(response.data['raw_response']);
    }
    response.data = objectToCamel(response.data);
  }

  return response;
});

export { extractServerName };

/**
 * Get guest user free trial quota status without consuming quota
 * Returns: { lifetimeLimit: number, used: number, remaining: number }
 * For paid users: remaining = -1 (unlimited indicator)
 */
export async function getGuestTrialStatus(): Promise<TrialStatusResponse> {
  try {
    const response = await AI_HTTP_CLIENT.get<TrialStatusResponse>(
      '/quiz-builder/trial-status',
    );
    return response.data;
  } catch (error) {
    logger.debug('failed_to_get_guest_trial_status', {
      error: getErrorMessage(error),
    });
    throw error;
  }
}
